import { useState, useEffect, useRef } from "react";
import { API , authFetch } from "@/App";
import { Bot, Send, X, MessageSquare, Loader2, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const AIAgentWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Load history on first open
  useEffect(() => {
    if (!isOpen || messages.length > 0) return;
    setMessages([{ role: "assistant", content: "Hello! I'm **Pravaah AI**, your intelligent assistant. Ask me about sales data, dealer performance, employee tracking, or any business insights." }]);
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const res = await authFetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ message: userMsg.content, session_id: sessionId }),
      });
      const data = await res.json();
      if (data.success) {
        setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I encountered an error. Please try again." }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Connection error. Please check your network." }]);
    }
    finally { setLoading(false); }
  };

  const formatMessage = (text) => {
    // Simple markdown: bold, bullet points, headers
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/^\- /gm, '<span class="text-[#8B1A1A] mr-1">&#8226;</span>')
      .replace(/\n/g, '<br/>');
  };

  if (!isOpen) {
    return (
      <button onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 w-14 h-14 rounded-full bg-[#1A1A2E] hover:bg-[#2a2a4e] text-white shadow-xl flex items-center justify-center transition-all hover:scale-110 z-50"
        data-testid="ai-widget-trigger">
        <Bot className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 right-6 w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col z-50 overflow-hidden"
      data-testid="ai-widget">
      {/* Header */}
      <div className="bg-[#1A1A2E] px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#8B1A1A] flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Pravaah AI</p>
            <p className="text-[10px] text-slate-400">Powered by Claude</p>
          </div>
        </div>
        <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="ai-widget-close">
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-xs leading-relaxed ${
              msg.role === "user"
                ? "bg-[#8B1A1A] text-white rounded-br-sm"
                : "bg-slate-100 text-slate-800 rounded-bl-sm"
            }`}
              data-testid={`ai-msg-${i}`}
              dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-xl px-3.5 py-2.5 rounded-bl-sm">
              <div className="flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
                <span className="text-[10px] text-slate-400">Thinking...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-slate-100 shrink-0">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Ask about sales, dealers, performance..."
            className="flex-1 text-xs px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:border-[#8B1A1A] transition-colors"
            disabled={loading}
            data-testid="ai-input"
          />
          <button onClick={sendMessage} disabled={loading || !input.trim()}
            className="w-8 h-8 rounded-lg bg-[#8B1A1A] hover:bg-[#6B1515] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
            data-testid="ai-send-btn">
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
        <p className="text-[9px] text-slate-400 mt-1.5 text-center">AI may produce inaccurate responses. Verify important data.</p>
      </div>
    </div>
  );
};

export default AIAgentWidget;
